import React, { useCallback, useEffect } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

//#region Components
import MinutesDraftEditor from './sectionlist/MinuteEditorListView'
import { isNil } from 'rambdax'
import ActionList from 'components/minutetaker/actionlist/ActionListView'
//#endregion

//#region Redux
import styled from '@emotion/styled'
import MinutesReview from './MinutesReview'
import { setSessionStorageItem } from 'businesslayer/minutesLocalStore'
import { SavingProvider } from 'components/contexts/saving'
import { useCommitteesState } from 'components/contexts/committee-context'
import { useMinutesPropertiesModal } from 'components/modals'
import { saveMinutesProperties, saveMinutesPropertiesMultipart } from 'businesslayer/api/minutes'
import blockingOperation from 'businesslayer/blockingOperation'
import { MuiThemeProvider } from '@material-ui/core'
import { materialAtlasTheme } from 'material-atlas/theme'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
//#endregion

//#region Props

enum SelectedTab {
    Taker = 0,
    Actions = 1
}

enum DocumentView {
    Draft = 0,
    InReview = 1,
    FinalDraft = 2,
    Approved = 3
}

type Props = {
    minutesActions: ActionItem[]
    onActionChecked: (...args: any[]) => void
    selectedTab: number
    currentMinuteItem: any
    onStatusChange: (status: DocumentStatusKey) => void
    history: any
    reloadActions: () => void
    setIsLoading: any
} & RouteComponentProps

//#endregion

//#region Implementation
const MinutesDocumentDetail = (props: Props) => {
    const { currentMinuteItem, history, onStatusChange } = props
    const { adminCommittees, currentCommittee } = useCommitteesState()
    const isAdmin = adminCommittees?.length > 0

    const handleSaveProperties = useCallback(
        async ({ updatedMinutesItem, logoFile }: { updatedMinutesItem: any; logoFile?: File }) => {
            if (!updatedMinutesItem) return
            const hasLogoFile = logoFile !== undefined
            const hasExistingLogo =
                updatedMinutesItem.id &&
                (updatedMinutesItem.logo || updatedMinutesItem.logo_filename)
            const shouldSendLogoFile = updatedMinutesItem.logoType === 'UPLOAD_LOGO' && hasLogoFile
            const needsMultipart = shouldSendLogoFile || hasExistingLogo

            try {
                if (needsMultipart) {
                    await saveMinutesPropertiesMultipart(
                        updatedMinutesItem,
                        shouldSendLogoFile ? logoFile : undefined,
                        false,
                        saveMinutesPropertiesMultipart.bind(
                            null,
                            updatedMinutesItem,
                            shouldSendLogoFile ? logoFile : undefined
                        )
                    )
                } else {
                    await saveMinutesProperties(
                        updatedMinutesItem,
                        false,
                        saveMinutesProperties.bind(null, updatedMinutesItem, false)
                    )
                }
                onStatusChange(currentMinuteItem.attributes.status)
            } catch (error) {
                console.error(
                    'Failed to save minutes properties:check correlation-id for more details'
                )
            } finally {
                blockingOperation.hideProgress()
            }
        },
        [currentMinuteItem, onStatusChange]
    )

    const {
        MinutesPropertiesModal: PropertiesModal,
        handleOpenMinutesModal
    } = useMinutesPropertiesModal({
        canAddMinutes: isAdmin,
        onSave: handleSaveProperties
    })

    const handleOpenProperties = useCallback(() => {
        const committeeId =
            currentMinuteItem?.relationships?.committee?.data?.id ||
            currentMinuteItem?.attributes?.committeeId
        const committeeName = currentCommittee?.name || ''
        handleOpenMinutesModal('EDIT', {
            id: currentMinuteItem.id,
            committee_id: committeeId,
            committee: { id: committeeId, name: committeeName }
        } as Partial<MinutesUnnormalized>)
    }, [handleOpenMinutesModal, currentMinuteItem, currentCommittee])

    useEffect(() => {
        // Set selectedActionId in sessionStorage based on the url
        history.listen((location) => {
            if (location.state && !isNil(location.state.actionId)) {
                setSessionStorageItem('selectedActionItemId', location.state.actionId)
            }
        })
    }, [history])

    useEffect(() => {
        // We should avoid reading/writing from the session storage like this unless we are
        // preserving state across page refresh. The problem is that storing the
        // selectedMinuteItemId in session storage was implemented this way since the beginning
        // and is tangled up in redux. This will require a tech debt story to refactor.
        setSessionStorageItem('selectedMinuteItemId', currentMinuteItem.id)
    }, [currentMinuteItem])

    const getDocumentComponent = (documentStatus: DocumentView) => {
        return documentStatus === DocumentView.InReview ? (
            // Review Page
            <MinutesReview
                onStatusChange={props.onStatusChange}
                minutesActions={props.minutesActions}
            />
        ) : (
            // Draft, Review (Pre Review Process), Final, Draft, Approved
            <MinutesDraftEditor
                onStatusChange={props.onStatusChange}
                onActionChecked={props.onActionChecked}
                setIsLoading={props.setIsLoading}
                isAdmin={isAdmin}
                onOpenProperties={isAdmin ? handleOpenProperties : undefined}
            />
        )
    }

    const showActions = props.selectedTab === SelectedTab.Actions
    // TODO: will be used for rendering (review, final draft, and approved) pages
    const currentDocumentView = determineDocumentView(currentMinuteItem.attributes.status)
    const DocumentComponent = getDocumentComponent(currentDocumentView)
    return (
        <>
            {/* We conditionally hide the Minutes Document in order to optimize the
                transition from Actions to Document view. Unmounting and Mounting the Document is expensive!
                TODO: future improvement virtual scroll or lazy load the sections on document. */}
            <SavingProvider>
                <DocumentDetailContainer isHidden={showActions}>
                    {DocumentComponent}
                </DocumentDetailContainer>
            </SavingProvider>
            {showActions && (
                <ActionList
                    onDocumentStatusChange={props.onStatusChange}
                    reloadActions={props.reloadActions}
                />
            )}
            {isAdmin && (
                <MuiThemeProvider theme={materialAtlasTheme}>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                        {PropertiesModal}
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
            )}
        </>
    )
}

const DocumentDetailContainer = styled('div')<{ isHidden: boolean }>`
    display: ${({ isHidden }) => (isHidden ? 'none' : 'inherit')};
    height: 100%;
`

const determineDocumentView = (status) => {
    return status === 'draft'
        ? DocumentView.Draft
        : status === 'in_review'
        ? DocumentView.InReview
        : status === 'final_draft'
        ? DocumentView.FinalDraft
        : status === 'approved'
        ? DocumentView.Approved
        : DocumentView.Draft
}

export default withRouter(MinutesDocumentDetail)

//#endregion
